import { useState } from 'react';
import { formatVND, formatDate } from '../../../../utils/format';
import { showToast } from '../../../../components/base/Toast';
import { useModalBackHandler } from '../../../../hooks/useModalBackHandler';
import type { AdvanceRequest } from '../../../../types';

interface LoanNeedMoreModalProps {
  loan: AdvanceRequest;
  farmerName: string;
  onClose: () => void;
  onNeedMore: (content: string, taiLieuCanBoSung: string, hanBoSung: string) => void;
}

export default function LoanNeedMoreModal({ loan, farmerName, onClose, onNeedMore }: LoanNeedMoreModalProps) {
  useModalBackHandler(true, onClose);
  const [content, setContent] = useState(loan.noiDungCanBoSung || '');
  const [taiLieu, setTaiLieu] = useState('');
  const [hanBoSung, setHanBoSung] = useState('');

  const handleSubmit = () => {
    if (!content.trim()) {
      showToast('Vui lòng nhập nội dung cần bổ sung', 'warning');
      return;
    }
    onNeedMore(content.trim(), taiLieu.trim(), hanBoSung);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40 animate-modal-backdrop" onClick={onClose} />
      <div onClick={(e) => e.stopPropagation()} className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto animate-modal-slide-up">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between z-10">
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-slate-900">Yêu cầu bổ sung</h3>
            <p className="text-xs text-slate-400 mt-0.5">{farmerName} · {formatDate(loan.ngayYeuCau)}</p>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-4 sm:px-6 py-4 space-y-4">
          <div className="bg-slate-50 rounded-xl p-3">
            <div className="flex items-start justify-between gap-2 text-sm">
              <span className="text-slate-500 shrink-0">Mục đích</span>
              <span className="font-medium text-slate-900 text-right break-words max-w-[60%]">{loan.mucDich}</span>
            </div>
            <div className="flex items-start justify-between gap-2 text-sm mt-1">
              <span className="text-slate-500 shrink-0">Số tiền</span>
              <span className="font-medium text-slate-900 text-right break-words max-w-[60%]">{formatVND(loan.soTien)}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Nội dung cần bổ sung <span className="text-red-500">*</span>
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              maxLength={500}
              rows={3}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500 resize-none"
              placeholder="Mô tả chi tiết những gì nông hộ cần bổ sung..."
            />
            <p className="text-xs text-slate-400 text-right">{content.length}/500</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Tài liệu cần bổ sung</label>
            <textarea
              value={taiLieu}
              onChange={(e) => setTaiLieu(e.target.value)}
              maxLength={300}
              rows={2}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500 resize-none"
              placeholder="VD: CMND/CCCD, sổ hộ khẩu, giấy chứng nhận quyền sử dụng đất..."
            />
            <p className="text-xs text-slate-400 text-right">{taiLieu.length}/300</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Hạn bổ sung</label>
            <input
              type="date"
              value={hanBoSung}
              onChange={(e) => setHanBoSung(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
            />
          </div>

          <div className="bg-amber-50 rounded-xl p-3">
            <div className="flex items-start gap-2">
              <span className="w-5 h-5 flex items-center justify-center text-amber-600 shrink-0"><i className="ri-information-line" /></span>
              <p className="text-xs text-amber-700">Sau khi gửi yêu cầu, trạng thái sẽ chuyển sang &quot;Cần bổ sung&quot;. Nông hộ cần cập nhật thông tin để tiếp tục quy trình duyệt.</p>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 sm:px-6 py-3 flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-md text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]"
          >
            Hủy
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-4 py-2.5 rounded-md text-sm font-medium text-white bg-amber-500 hover:bg-amber-600 transition-colors whitespace-nowrap min-h-[44px]"
          >
            <span className="w-4 h-4 inline-flex items-center justify-center mr-1"><i className="ri-send-plane-line" /></span>
            Gửi yêu cầu bổ sung
          </button>
        </div>
      </div>
    </div>
  );
}